import { QuestionModalProps, QuestionTypeMeta } from "../BaseQuestionModal";
import { Button } from "melodies-source/Button";
import * as yup from "yup";
import { REQUIRED_MSG } from "Utils/yupStrictPassword";
import { Field, FieldProps, Form, Formik } from "formik";
import { useMemo } from "react";
import { HTMLModule, TextModule } from "@max/common/dist/setfan";
import { ResponsiveFooterActions } from "Components/ResponsiveModal";
import "react-quill/dist/quill.snow.css";
import styled from "styled-components";
import { QuillHtml, quillStylesheet } from "Routes/SetFan/Components/quill";
import { QuillEditor } from "Components";
import { useConfigSlice } from "Routes/SetFan/Components/BuilderContext";
import { hexToArrayRGB } from "Utils";

interface Values {
  label: string;
}

const VALIDATION_SCHEMA = yup.object().shape({
  label: yup.string().required(REQUIRED_MSG),
  caption: yup.string(),
});

const AddTextModal = ({
  dispatch,
  existing,
  onClose,
}: Omit<QuestionModalProps<TextModule>, "state">) => {
  const { fields } = useConfigSlice(["buttonColor"]);
  const buttonColorRGB =
    fields.buttonColor.content && hexToArrayRGB(fields.buttonColor.content);
  const isButtonColorDark =
    buttonColorRGB &&
    0.2126 * buttonColorRGB[0] +
      0.7152 * buttonColorRGB[1] +
      0.0722 * buttonColorRGB[2] <
      128;

  const initialValues: Values = useMemo(
    () => ({
      label: existing?.label.en ?? "",
      stylesheet: existing?.stylesheet ?? "",
    }),
    [existing],
  );

  const onSubmit = ({ label }: Values) => {
    const htmlModule = new HTMLModule({
      className: "quill-html",
      id: existing?.id,
      label: { en: label },
      style: { paddingBottom: 20 },
      stylesheet: quillStylesheet,
    });

    dispatch(
      existing
        ? {
            type: "updateModule",
            payload: { idToReplace: existing.id, module: htmlModule },
          }
        : {
            type: "addModule",
            payload: { module: htmlModule },
          },
    );
    onClose();
  };

  return (
    <StyledQuillHtml>
      <Formik<Values>
        initialValues={initialValues}
        validationSchema={VALIDATION_SCHEMA}
        onSubmit={onSubmit}
      >
        <Form>
          <div style={{ marginBottom: 26 }}>
            <Field name="label">
              {({ field, form }: FieldProps) => (
                <>
                  <QuillEditor
                    value={field.value}
                    onChange={(value) => form.setFieldValue("label", value)}
                    linkColor={
                      isButtonColorDark
                        ? fields.buttonColor.content
                        : undefined
                    }
                  />
                </>
              )}
            </Field>
          </div>
          <ResponsiveFooterActions>
            <Button type="button" variant="tertiary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit">{existing ? "Update" : "Add"}</Button>
          </ResponsiveFooterActions>
        </Form>
      </Formik>
    </StyledQuillHtml>
  );
};

export const text: QuestionTypeMeta = {
  icon: null,
  key: "text",
  header: "Text",
  subHeader: "",
  body: AddTextModal,
  variant: "small",
};

const StyledQuillHtml = styled(QuillHtml)`
  .ql-container {
    font-family: Poppins;
  }
`;
